import React, { useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { Link } from '@fs/zion-router'
import { shape, string, number, oneOfType, bool } from 'prop-types'
import { useTranslation } from 'react-i18next'
import CardType from './CardType'
import { ColorThemeType } from '../../sharedPropTypes/activityProps'
import { populateActivityGroupUrl } from '../../helpers/activities_helper'
import { FamilyGroupType } from '../../sharedPropTypes/familyGroupProps'
import { snowFramedPaths, appendQueryParams, getActivitiesReturnUrl } from '../../helpers/url_helper'
import useCardTransition from '../../hooks/useCardTransition'
import { usePWAMode } from '../../hooks/appRootHooks'
import getIsTWAMode from '../../helpers/appMode_helper'
import { getActivityCoverColor } from '../../constants/activityCoverColors'
import ActivityLaunchCover from '../ActivityLaunchCover'

const cardPressCss = css`
  transition: transform 0.1s ease-out;
  &:active {
    transform: scale(0.96);
  }
`

// returnTo — gives a return URL that the app can use for an X button
// twa — signals to the app that we are in the TWA instead of solely relying on a shared sessionStorage key.
// coverLabel — the label on our cover, so discovery-react can show the same words in the
//   same language from its very first paint, before its own translations have loaded.
function buildLinkParams({ stayInApp, isTWAMode, isSiblingDiscoveryApp, coverColor, coverLabel }) {
  const params = {}
  if (stayInApp) params.returnTo = getActivitiesReturnUrl()
  if (isTWAMode && isSiblingDiscoveryApp) params.twa = '1'
  if (coverColor) params.coverLabel = coverLabel
  return params
}

function KidsCard({ data, topicId, familyGroup, showRoman, useAsListItem = false }) {
  const { url, dataType, splash, link, visibleId, person, count, inAppExternal } = data || {}
  const [t] = useTranslation()
  const isPWA = usePWAMode()
  const isTWAMode = getIsTWAMode()

  // Use shared transition logic (handles animation and external link checks)
  const { cardRef, handleTransitionClick } = useCardTransition(data)

  const toUrl = useMemo(() => {
    if (url) {
      return url
    }
    return dataType === 'family-avatar'
      ? '/activities/familyavatars'
      : populateActivityGroupUrl({
          splash,
          link,
          visibleId,
          topicId,
          familyGroup,
          personId: person?.personId,
        })
  }, [url, dataType, splash, link, visibleId, topicId, familyGroup, person])

  // splash:'external' cards navigate away from the app entirely.
  // For Snow-framed paths in PWA, we let React Router render the link (useRouterLink=true) so the app
  // basename is included in href — useExternalLinks then sends the full discovery-kids URL to the TWA
  // scheme, preventing the Tree app from intercepting the bare /temple/completed path.
  const isSnowFramedPath = splash === 'external' && snowFramedPaths.includes(toUrl)
  const useRouterLink = isSnowFramedPath && isPWA

  // inAppExternal cards point at separate app (normally discovery-react) that is served from the same
  // origin in beta/prod. We keep external for full-page navigation instead of client-side routing. target="_self"
  // used for PWA/TWA to keep the navigation inside the WebView and avoid Custom Tab handoff. In a normal browser
  // these open in a new tab (_blank).
  const isExternal = splash === 'external'
  const stayInApp = isExternal && inAppExternal && isPWA
  const linkTarget = isExternal && !stayInApp ? '_blank' : '_self'

  // In the TWA, show a color matching the destination discovery-react background for a smoother handoff.
  const coverColor = isTWAMode && stayInApp ? getActivityCoverColor(visibleId) : null

  const coverLabel = t('common.loading', 'Loading')
  const [launching, setLaunching] = useState(false)

  const isSiblingDiscoveryApp = isExternal && inAppExternal
  const linkParams = buildLinkParams({ stayInApp, isTWAMode, isSiblingDiscoveryApp, coverColor, coverLabel })
  const linkTo = Object.keys(linkParams).length > 0 ? appendQueryParams(toUrl, linkParams) : toUrl

  const handleClick = (e) => {
    // Leave modified and non-primary clicks to the browser so opening in a new tab still works.
    if (coverColor && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault()
      setLaunching(true)
      return
    }

    handleTransitionClick(e, toUrl)
  }

  return (
    <li
      ref={cardRef}
      css={isTWAMode ? cardPressCss : undefined}
      style={{ maxWidth: '160px', marginInlineEnd: '8px', listStyleType: 'none' }}
      role={useAsListItem ? 'listitem' : 'presentation'} // disable listItem semantics if not used in a list
    >
      <Link
        noLinkStyles
        to={linkTo}
        linkName={`ActivityGroupCard:CARD_${visibleId}:CLICK`}
        external={splash === 'external' && !useRouterLink}
        target={linkTarget}
        aria-labelledby={`card-label-${data?.person?.personId ?? data?.activityGroupId ?? data?.id}`} // use id for campaign cards
        aria-describedby={count ? `card-completed-count-${data?.person?.personId ?? data?.activityGroupId}` : null}
        onClick={handleClick}
      >
        <CardType type={dataType} {...data} showRoman={showRoman} />
      </Link>
      {launching && (
        <ActivityLaunchCover
          color={coverColor}
          label={coverLabel}
          onNavigate={() => window.location.assign(linkTo)}
          onDismiss={() => setLaunching(false)}
        />
      )}
    </li>
  )
}

KidsCard.propTypes = {
  data: shape({
    url: string,
    dataType: string.isRequired,
    disableSplashTransition: bool,
    link: string,
    visibleId: oneOfType([string, number]),
    splash: string,
    inAppExternal: bool,
    imageUrl: string,
    imageUrlSvg: string,
    colorTheme: ColorThemeType,
    person: shape({
      personId: string,
      name: string,
      portraitUrl: string,
    }),
    count: oneOfType([number, string]),
    activityGroupId: string,
    id: string,
  }).isRequired,
  topicId: oneOfType([string, number]),
  familyGroup: FamilyGroupType,
  showRoman: bool,
  useAsListItem: bool,
}

export default React.memo(KidsCard)
